/**
 * Tasks SSE Connection Manager
 *
 * A plain TS module (no React, no store) that manages a single shared SSE connection
 * to the Tasks stream endpoint (`GET /rovo/v1/tasks/stream`).
 *
 * Key design properties:
 * - **Single connection**: only one SSE connection is open at a time, regardless of
 *   how many consumers are subscribed.
 * - **Reference-counted**: connection opens when the first subscriber arrives,
 *   closes when the last subscriber leaves.
 * - **Tab-visibility aware**: connection is dropped when the tab becomes hidden and
 *   re-established when the tab becomes visible again. This is fully managed here,
 *   not by any React component.
 * - **Decoupled from React lifecycle**: module-level state survives component
 *   mount/unmount cycles.
 * - **External trigger support**: can subscribe
 *  by providing `getService` explicitly — no component mounting required.
 */

import { bind } from 'bind-event-listener';

import { getDocument } from '@atlaskit/browser-apis';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import type {
	AssistanceService,
	ApiTaskSSEUpdate,
	ApiTaskSSEEventType,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { isSSEMessage } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

export type TasksSSEListener = (event: ApiTaskSSEUpdate, eventType: ApiTaskSSEEventType) => void;

// ─── Module-level state (singleton) ──────────────────────────────────────────

/** Active SSE fetch abort controller. Null when disconnected. */
let abortController: AbortController | null = null;

/** The AssistanceService instance used for the current (or next) connection. */
let serviceRef: AssistanceService | null = null;

/** All active subscribers. Connection is open iff this Set is non-empty. */
const subscribers = new Set<TasksSSEListener>();

/** Cleanup function for the visibilitychange listener. Null when unbound. */
let visibilityUnbind: (() => void) | null = null;

// ─── Internal helpers ─────────────────────────────────────────────────────────

/**
 * Opens the SSE connection using the stored `serviceRef`.
 * Fans out each incoming event to all current subscribers.
 * Safe to call if a connection is already open (no-op guard via abortController).
 */
async function openConnection(): Promise<void> {
	if (!serviceRef) {
		return;
	}

	// Prevent duplicate connections
	if (abortController) {
		return;
	}

	abortController = new AbortController();
	const localAbort = abortController;

	try {
		const { generator } = await serviceRef.getTasksStream(localAbort);

		for await (const message of generator) {
			// Bail out if connection was closed while we were iterating
			if (localAbort.signal.aborted) {
				break;
			}

			// The SSE stream yields ParsedSSEMessage<ApiTaskSSEUpdate> wrappers.
			// The event_type is carried in the SSE `event:` envelope line → message.eventType.
			if (!isSSEMessage<ApiTaskSSEUpdate>(message)) {
				continue;
			}

			const event = message.data;
			const eventType = (message.eventType ?? 'task_status') as ApiTaskSSEEventType;

			if (event) {
				subscribers.forEach((listener) => listener(event, eventType));
			}
		}
	} catch (error) {
		// AbortError is expected on intentional disconnect — swallow it silently
		if (error instanceof DOMException && error.name === 'AbortError') {
			return;
		}
		// Report unexpected errors to Sentry. We deliberately do NOT re-throw:
		// `openConnection` is invoked via `void openConnection()` at every call
		// site, so a thrown error would surface as an unhandled promise rejection
		// — which can crash strict hosts and would otherwise tear down the chat
		// session for what is only a side-channel failure.
		logException({
			name: 'rovoTasksSSEConnection',
			exception: error,
		});
	} finally {
		// Ensure controller is cleared once the loop exits for any reason
		if (abortController === localAbort) {
			abortController = null;
		}
	}
}

/**
 * Aborts the current SSE connection.
 * Safe to call when already disconnected.
 */
function closeConnection(): void {
	if (abortController) {
		abortController.abort();
		abortController = null;
	}
}

/**
 * Binds a `visibilitychange` listener on the document.
 * - Tab hidden  → close connection
 * - Tab visible → re-open connection (reuses stored serviceRef)
 *
 * Binding is done once per subscriber session (first subscriber triggers it).
 */
function bindVisibility(): void {
	if (visibilityUnbind) {
		// Already bound — nothing to do
		return;
	}

	const doc = getDocument();
	if (!doc) {
		return;
	}

	visibilityUnbind = bind(doc, {
		type: 'visibilitychange',
		listener: () => {
			if (doc.hidden) {
				closeConnection();
			} else {
				// Re-open connection when tab becomes visible again
				void openConnection();
			}
		},
	});
}

/**
 * Removes the `visibilitychange` listener.
 * Called when the last subscriber unsubscribes.
 */
function unbindVisibility(): void {
	visibilityUnbind?.();
	visibilityUnbind = null;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Subscribes to Tasks SSE updates.
 *
 * @param getService - A function returning the `AssistanceService` instance to use.
 *   - **Internal callers** (React components): pass `() => service` where `service`
 *     comes from `useAssistanceService()`.
 *   - **External callers** (host apps): pass
 *     `() => new AssistanceServiceImpl(explicitConfig)`.
 *
 *   Called once on the first subscription (or on reconnect if no service is stored).
 *   The resolved instance is stored as `serviceRef` for reconnects after tab
 *   visibility changes.
 *
 * @param onEvent - Callback invoked with each `ApiTaskSSEUpdate` event.
 *
 * @returns An `unsubscribe` function. Call it to stop receiving events.
 *   When the last subscriber unsubscribes, the SSE connection is closed automatically.
 *
 * @example — Internal (React component):
 * ```ts
 * const service = useAssistanceService();
 * useEffect(() => {
 *   return subscribeToTasksSSE(() => service, (event) => handleEvent(event));
 * }, [service]);
 * ```
 *
 * @example — External
 * ```ts
 * const unsubscribe = subscribeToTasksSSE(
 *   () => new AssistanceServiceImpl({ product: 'jira', experienceId: 'rovo-button', headers }),
 *   (event) => updateBadgeCount(event),
 * );
 * ```
 */
export function subscribeToTasksSSE(
	getService: () => AssistanceService,
	onEvent: TasksSSEListener,
): () => void {
	subscribers.add(onEvent);

	if (subscribers.size === 1) {
		// First subscriber — resolve service, open connection, bind visibility
		serviceRef = getService();
		bindVisibility();

		// Only open connection if tab is currently visible
		const doc = getDocument();
		if (!doc || !doc.hidden) {
			void openConnection();
		}
	}

	return function unsubscribe(): void {
		subscribers.delete(onEvent);

		if (subscribers.size === 0) {
			// Last subscriber left — tear everything down
			closeConnection();
			unbindVisibility();
			serviceRef = null;
		}
	};
}
